import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useSignal, VegaChartSignal } from '@asset-metrix/portal-ui';
import { Spec, View } from 'vega';

import { getPageBoundaries } from '../../utils/vegaChartHelpers';

export const PAGINATION_ENTRIES_DATA_NAME = 'paginationEntries';

export const useWithPagination = (spec: Spec) =>
  useMemo(
    () =>
      spec.signals?.some((s) => s.name === VegaChartSignal.pageBoundaries) ??
      false,
    [spec]
  );

const hasCustomPages = (spec: Spec) =>
  spec.data?.find((d) => d.name === PAGINATION_ENTRIES_DATA_NAME);

export const useItemCount = (
  spec: Spec,
  vegaViewRef: RefObject<View>,
  data?: unknown
) => {
  const [itemCount, setItemCount] = useState<number>();

  const listener = useCallback((_: unknown, newData: unknown) => {
    if (Array.isArray(newData)) {
      setItemCount(newData.length);
    }
  }, []);

  useEffect(() => {
    const currentRef = vegaViewRef.current;

    if (hasCustomPages(spec)) {
      currentRef?.addDataListener(PAGINATION_ENTRIES_DATA_NAME, listener);
    }

    return () => {
      if (hasCustomPages(spec)) {
        currentRef?.removeDataListener(PAGINATION_ENTRIES_DATA_NAME, listener);
      }
    };
  }, [listener, vegaViewRef, spec]);

  const onReadyCallback = useCallback(
    async (newView?: View) => {
      // listeners do not get called for initial internal calculation of the data
      if (hasCustomPages(spec)) {
        await vegaViewRef.current?.runAsync();
        const count = vegaViewRef.current?.data(
          PAGINATION_ENTRIES_DATA_NAME
        ).length;

        setItemCount(count);
      } else if (Array.isArray(data)) {
        setItemCount(data.length);
      }

      // renew listeners when the callback is called. this happens sometimes after the initial render (or after theme switch).
      // the effects will trigger the first time before the vegaViewRef.current is set
      if (hasCustomPages(spec)) {
        newView?.addDataListener(PAGINATION_ENTRIES_DATA_NAME, listener);
      }
    },
    [data, listener, spec, vegaViewRef]
  );

  useEffect(() => {
    // also call onReadyCallback when its dependencies change, in case the onReady call of the vega view has already happened previously
    onReadyCallback();
  }, [onReadyCallback]);

  return { itemCount, onReadyCallback };
};

export const usePageBoundaries = (
  withPagination: boolean,
  vegaViewRef: RefObject<View>,
  spec: Spec,
  rawData: unknown
) => {
  const signal = useSignal(vegaViewRef);

  const [currentPage, setCurrentPage] = useState(0);

  const { itemCount, onReadyCallback: onReadyCallbackForItemCount } =
    useItemCount(spec, vegaViewRef, rawData);

  const pageBoundaries = useMemo(
    () => getPageBoundaries(currentPage, itemCount),
    [currentPage, itemCount]
  );

  useEffect(() => {
    if (withPagination) {
      signal(VegaChartSignal.pageBoundaries, pageBoundaries);
    }
  }, [pageBoundaries, signal, withPagination]);

  const onReadyCallback = useCallback(
    (newView: View) => {
      onReadyCallbackForItemCount(newView);
      // this is required as the effect triggers before the view is ready, and the initial page is never set otherwise
      if (withPagination) {
        signal(VegaChartSignal.pageBoundaries, pageBoundaries);
      }
    },
    [onReadyCallbackForItemCount, pageBoundaries, signal, withPagination]
  );

  return {
    onReadyCallback,
    currentPage,
    setCurrentPage,
    itemCount: itemCount ?? 0,
  };
};
